<script lang="ts" setup>
	import { format } from 'date-fns'
	import type { TabGroupContext } from '~/components/ui/tab-group.vue'
	import type { BlogPostContext, BlogQuery } from '~/server/type'
	import { useIntersectionObserver } from '@vueuse/core'

	const selectedCategory = shallowRef('all')
	const filterValue = shallowRef<Date | null>(null)
	const { getAllPost, getPostCategories } = useBlogApi()
	const { wordLimit } = useUtils()
	const loading = shallowRef(false)
	const posts = useState<BlogPostContext[]>('blog-posts', () => [])
	const currentPage = ref(1)
	const isLoadMore = ref(false)
	const maxDate = ref<Date | null>(null)

	onMounted(() => {
		maxDate.value = new Date()
	})
	const pagination = useState('blog-pagination', () => ({
		total: 0,
		totalPages: 0,
		perPage: 12
	}))

	const sentinel = useTemplateRef('sentinel')

	const fetchPosts = async (query?: BlogQuery & { append?: boolean }) => {
		try {
			const { append = false, ...restQuery } = query || {}
			if (append) {
				isLoadMore.value = true
			} else {
				loading.value = true
			}

			const result = await getAllPost({
				page: currentPage.value,
				per_page: pagination.value.perPage,
				...restQuery
			})

			if (result) {
				const newPosts = result.items || []
				posts.value = append ? [...posts.value, ...newPosts] : newPosts
				pagination.value = {
					...pagination.value,
					total: Number(result.total_count || 0),
					totalPages: result.total_pages || 0
				}
				return newPosts
			}
			if (!append) posts.value = []
			return []
		} finally {
			loading.value = false
			isLoadMore.value = false
		}
	}
	const { status, data } = await useAsyncData(() => Promise.allSettled([fetchPosts(), getPostCategories()]))
	const isLoading = computed(() => status.value === 'pending' || loading.value)

	const categories = computed(() => {
		const categories = data.value?.[1] ?? null
		if (categories?.status === 'fulfilled') {
			const list = categories.value.map(item => ({
				label: String(item.slug).replace('-', ' '),
				id: item.id,
				value: item.id
			})) as TabGroupContext

			return [{ label: 'All', id: 0, value: 'all' }, ...list]
		}
		return []
	})
	const logFilter = (arg: { content: TabGroupContext[number] | null }) => {
		if (typeof arg.content === 'object' && arg.content?.id !== undefined) {
			const query = arg.content.id ? { categories: arg.content.id } : {}
			selectedCategory.value = arg.content.id
			fetchPosts({ ...query, ...filterDate() })
		}
	}
	const filterDate = () => {
		if (filterValue.value) {
			return { date: format(filterValue.value, 'yyyy-MM-dd') }
		}
		return {}
	}
	const search = (arg: { search?: string }) => {
		const { search } = arg
		selectedCategory.value = 'all'
		fetchPosts({ search, ...filterDate() })
	}

	const { generateBlogSeriesSchema } = useSeoSchema()

	useHead({
		script: [
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(generateBlogSeriesSchema(posts.value))
			}
		],
		link: [{ rel: 'canonical', href: 'https://mycovergenius.com/blog' }]
	})

	useSeoMeta({
		title: 'Blog - MyCoverGenius | Insurance & Insurtech Insights',
		description: 'Stay updated with the latest in insurance, tech, and productivity on the MyCoverGenius blog.',
		ogTitle: 'Blog - MyCoverGenius | Insurance & Insurtech Insights',
		ogDescription: 'Stay updated with the latest in insurance, tech, and productivity on the MyCoverGenius blog.',
		ogImage: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082913/team-office_im9gfo.webp',
		ogUrl: 'https://mycovergenius.com/blog',
		ogLocale: 'en_NG',
		twitterTitle: 'Blog - MyCoverGenius | Insurance & Insurtech Insights',
		twitterDescription:
			'Stay updated with the latest in insurance, tech, and productivity on the MyCoverGenius blog.',
		twitterImage: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082913/team-office_im9gfo.webp',
		twitterCard: 'summary_large_image',
		twitterSite: '@MyCoverGenius',
		twitterCreator: '@MyCoverGenius',
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})

	watch(
		data,
		arg => {
			const productsResult = arg?.[0]
			if (productsResult?.status === 'fulfilled' && productsResult.value) {
				if (posts.value.length === 0) {
					posts.value = productsResult.value as BlogPostContext[]
				}
			}
		},
		{ immediate: true }
	)

	watch(filterValue, date => {
		currentPage.value = 1
		if (date) {
			selectedCategory.value = 'all'
			fetchPosts(filterDate())
		} else {
			fetchPosts()
		}
	})

	const loadMore = () => {
		if (isLoading.value || isLoadMore.value || currentPage.value >= pagination.value.totalPages) return

		currentPage.value++
		const selectedCat = categories.value.find(c => c.value === selectedCategory.value)
		const categoryQuery = selectedCategory.value !== 'all' && selectedCat ? { categories: selectedCat.id } : {}
		fetchPosts({ ...categoryQuery, ...filterDate(), append: true })
	}

	useIntersectionObserver(sentinel as any, ([{ isIntersecting }]) => {
		if (isIntersecting) {
			loadMore()
		}
	})
</script>

<template>
	<div id="blog-page">
		<header
			class="md:min-h-[420px] min-h-[300px] sm:min-h-[400px] relative mt-0 bg-base-800 dark:bg-base-900 pt-12 xs:pt-16"
		>
			<ui-pad>
				<ui-h1
					tag="Blog"
					:tag-icon="{ dir: 'document', icon: 'book' }"
					paragraph="Get regular updates on how to protect your business with insurance!"
					secondary-btn-text="Speak to us to get on board"
					class="md:flex items-center flex-col space-y-3 justify-center text-left"
					:ui="{
						btn: { base: 'hidden' },
						paragraphClass: {
							maxWidth: '',
							text: 'text-base max-md:pb-28'
						},
						hClass: {
							wrapper: {
								size: 'max-sm:max-w-[325px] text-[2.75rem]/[3.135rem] -tracking-[0.08rem] md:text-[3.5rem]/[3.99rem] md:-tracking-[0.1rem] lg:text-[4.25rem]/[4.845rem] lg:-tracking-[0.1275rem]'
							}
						},
						tag: {
							base: 'bg-main md:bg-[rgba(255,255,255,0.2)] text-white backdrop-blur-[10px]',
							default: '',
							font: 'font-[700]',
							size: 'lg:text-xl text-xs',
							icon: { display: 'md:hidden' },
							padding: 'md:p-[12px_24px] p-[4px_12px]',
							rounded: 'rounded-[20px]'
						}
					}"
				>
					<template #title="{ highlightColor }">
						<span> The <span :class="highlightColor">Genius</span> Blog </span>
					</template>
				</ui-h1>
			</ui-pad>
		</header>

		<ui-spacer :ui="{ marginBlock: 'my-20' }" />

		<main id="main-content">
			<ui-pad>
				<div
					class="inline-flex items-center md:w-auto w-full gap-3 flex-wrap"
					role="search"
					aria-label="Blog Search and Filter"
				>
					<molecules-search-field
						class="md:max-w-[265px] w-full"
						aria-label="Search articles"
						@change="arg => search({ search: arg })"
					/>

					<tools-date-picker v-model="filterValue" mode="month" :max-date="maxDate">
						<button
							type="button"
							name="filter"
							class="px-[9px] md:hidden py-[11px] flex-none h-[44px] flex flex-col border border-gray-300 bg-white rounded-lg items-center justify-center"
						>
							<tools-icon dir="arrows" icon="ellipsis" :height="21" :width="21" />
						</button>
					</tools-date-picker>
				</div>

				<div
					class="sm:flex justify-between px-2 gap-3 sm:flex-nowrap flex-wrap flex-1 items-stretch text-main w-full"
				>
					<client-only>
						<ui-tab-group
							:list="categories"
							v-model:value="selectedCategory"
							class="overflow overflow-x-auto invisible-scrollbar w-full pr-10"
							:ui="{
								tabProps: {
									wrapper: '',
									list: {
										base: 'w-full md:w-auto bg-transparent !inline-flex gap-x-3',
										height: '',
										marker: {
											base: ''
										},
										tab: {
											height: '',
											active: 'bg-main text-white font-medium',
											font: 'font-medium capitalize',
											shadow: '',
											background: '',
											padding: 'md:p-[10px_12px] p-[8px]',
											base: 'flex flex-1 border',
											rounded: 'rounded-[60px]',
											inactive: 'bg-white text-gray-600'
										}
									}
								}
							}"
							@change="({ content }) => logFilter({ content })"
						/>
						<template #fallback>
							<div class="flex gap-x-3 overflow-x-auto invisible-scrollbar w-full pr-10">
								<ui-skeleton v-for="i in 5" :key="`tab-fallback-${i}`" class="h-10 w-24 rounded-[60px] flex-none" />
							</div>
						</template>
					</client-only>

					<div class="ml-auto hidden md:block relative flex-1 whitespace-nowrap">
						<tools-date-picker v-model="filterValue" mode="date" :max-date="maxDate">
							<button
								type="button"
								aria-label="filter"
								class="h- border border-gray-300 text-gray-600 font-medium p-3 lg:p-[12px_24px] inline-flex rounded-lg gap-3 focus:ring-2 focus:ring-main"
							>
								<client-only>
									Filter By {{ !filterValue ? '' : format(filterValue, 'dd-MMM') }}
									<template #fallback> Filter By </template>
								</client-only>
							</button>
							<template #calendar-footer="{ close }">
								<div v-show="filterValue" class="w-full px-4 pb-5">
									<button
										type="button"
										aria-label="reset date"
										class="border-base-primary border hover:bg-base-primary hover:text-white text-base-primary font-bold w-full px-3 py-1 rounded-md"
										@click=";[(filterValue = null), close()]"
									>
										Reset
									</button>
								</div>
							</template>
						</tools-date-picker>
					</div>
				</div>

				<div
					class="sm:py-24 py-14 grow flex flex-wrap items-stretch lg:flex sm:grid place-content-center xl:place-content-start sm:grid-cols-2 gap-10"
				>
					<template v-if="isLoading">
						<ui-skeleton v-for="i in 3" :key="`loading-${i}`" class="w-full" />
					</template>
					<template v-else>
						<molecules-article-card
							v-for="(post, index) in posts"
							:key="index"
							:to="`/blog/${post?.slug ?? ''}_${post?.id ?? ''}`"
							:img-src="post?.media?.src ?? ''"
							:title="post?.title || ''"
							:exercept="wordLimit(post?.excerpt, 12)"
							:category="post?.categories || []"
							:loading="isLoading"
							:ui="{
								base: 'mx-auto md:mx-0',
								width: 'max-w-[422px] w-full lg:basis-[calc(50%-210px)] xl:basis-[calc(50%-255px)]',
								content: {
									article: {
										base: '',
										excerpt: {
											base: 'text-pretty'
										}
									}
								}
							}"
						/>
					</template>
				</div>

				<div ref="sentinel" class="flex flex-col items-center justify-center py-10 min-h-[100px]">
					<div v-if="isLoadMore" class="flex flex-col items-center gap-2">
						<tools-icon
							dir="loader"
							icon="loader"
							class="animate-spin text-main"
							:width="32"
							:height="32"
						/>
						<span class="text-sm text-gray-500 font-medium">Fetching more articles...</span>
					</div>
					<div
						v-else-if="currentPage >= pagination.totalPages && posts.length > 0"
						class="text-gray-400 text-sm"
						aria-live="polite"
					>
						You've reached the end of our updates.
					</div>
				</div>
			</ui-pad>
		</main>
	</div>
</template>
